import React, { useState } from "react";
import styled from "styled-components";
import JSZip from "jszip";
import toast from "react-hot-toast";
import {
  useGetProfileQuery,
  useGetActiveSessionsQuery,
  useTerminateSessionMutation,
  useLogoutAllOtherSessionsMutation,
  useGetTraderTariffsQuery,
  useGet2FAStatusQuery,
  useSetup2FAMutation,
  useVerify2FASetupMutation,
  useDisable2FAMutation,
  useRegenerateBackupCodesMutation,
  useChangePasswordMutation,
} from "../store/trader-api";

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: 0.75rem 0.75rem 0 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "var(--bg-card)" : "transparent")};
  color: ${(props) => (props.active ? "var(--text-primary)" : "var(--text-secondary)")};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
    background: ${(props) =>
      props.active ? "var(--bg-card)" : "var(--bg-hover)"};
  }
`;

const TabContent = styled.div`
  background: transparent;
  border-radius: 0 0 0.75rem 0.75rem;
  padding: 1rem 0 2rem 0;
  min-height: 400px;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SettingsCard = styled.div`
  background: var(--bg-primary);
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid var(--border-color);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardIcon = styled.span`
  font-size: 1.25rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const Badge = styled.span<{ variant?: "success" | "warning" | "error" }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;

  ${({ variant = "success" }) => {
    switch (variant) {
      case "success":
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
        `;
      case "warning":
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
        `;
      case "error":
        return `
          background: var(--status-error-bg);
          color: var(--accent-error);
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-secondary);
        `;
    }
  }}
`;

const ActionButton = styled.button<{
  variant?: "primary" | "secondary" | "danger";
}>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${({ variant = "primary" }) => {
    switch (variant) {
      case "primary":
        return `
          background: var(--accent-primary);
          color: #ffffff;
          &:hover {
            background: var(--accent-primary-hover);
          }
        `;
      case "secondary":
        return `
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border: 1px solid var(--border-color);
          &:hover {
            background: var(--border-color);
            color: var(--text-primary);
          }
        `;
      case "danger":
        return `
          background: var(--accent-error);
          color: #ffffff;
          &:hover {
            background: var(--alert-error-text);
          }
        `;
    }
  }}
`;

// Новый стилизованный компонент для выбираемых кнопок экспорта
const SelectableExportButton = styled.button<{
  selected?: boolean;
  disabled?: boolean;
}>`
  padding: 0.75rem 1rem;
  border: 2px solid ${(props) => (props.selected ? "var(--accent-primary)" : "var(--border-color)")};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  background: ${(props) => (props.selected ? "var(--alert-warning-bg)" : "var(--bg-card)")};
  color: ${(props) => (props.selected ? "var(--alert-warning-text)" : "var(--text-primary)")};

  &:hover:not(:disabled) {
    background: ${(props) => (props.selected ? "var(--alert-warning-border)" : "var(--bg-primary)")};
    border-color: ${(props) => (props.selected ? "var(--accent-primary-hover)" : "var(--border-input)")};
  }

  &:disabled {
    background: var(--bg-secondary);
    color: var(--text-muted);
    border-color: var(--border-color);
  }
`;

const SessionsTable = styled.div`
  margin-top: 1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const SessionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  min-width: 500px;

  &:hover {
    background: var(--bg-primary);
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    gap: 0.5rem;
    padding: 0.625rem;
  }
`;

const SessionHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 500px;

  @media (max-width: 768px) {
    font-size: 0.6875rem;
    gap: 0.5rem;
    padding: 0.625rem;
  }
`;

const TariffTable = styled.div`
  background: transparent;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TariffRow = styled.div`
  display: grid;
  grid-template-columns: 200px 120px 120px 100px 1fr 100px;
  gap: 0.75rem;
  padding: 1rem;
  padding-right: 20px;
  border-bottom: 1px solid var(--border-color);
  align-items: stretch;
  background: transparent;
  min-width: 700px;

  > div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    min-height: 24px;
    padding: 0;
    margin: 0;
  }

  > div:last-child {
    justify-content: center;
  }

  &:hover {
    background: var(--bg-card-hover);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TariffHeader = styled.div`
  display: grid;
  grid-template-columns: 200px 120px 120px 100px 1fr 100px;
  gap: 0.75rem;
  padding: 1rem;
  padding-right: 20px;
  background: var(--bg-glass);
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid var(--border-color);
  align-items: stretch;
  min-width: 700px;

  > div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    min-height: 24px;
    padding: 0;
    margin: 0;
  }

  > div:last-child {
    justify-content: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.875rem;
`;



const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 500px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1.25rem;
    width: 95vw;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-card);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

function Settings() {
  const [activeTab, setActiveTab] = useState<"general" | "tariffs" | "security">(
    "general"
  );



  // Состояния для экспорта
  const [exportForm, setExportForm] = useState({
    format: "CSV",
    dateFrom: "",
    dateTo: "",
  });

  // Состояние для выбранных типов экспорта
  const [selectedExportTypes, setSelectedExportTypes] = useState<Set<string>>(
    new Set()
  );
  const { data: profile } = useGetProfileQuery();
  const { data: sessions } = useGetActiveSessionsQuery();

  // Получаем тарифы трейдера
  const {
    data: tariffs,
    error: tariffsError,
    isLoading: tariffsLoading,
  } = useGetTraderTariffsQuery(profile?.id || "", {
    skip: !profile?.id,
  });

  // 2FA
  const { data: twoFAStatus, refetch: refetch2FA } = useGet2FAStatusQuery();
  const [setup2FA] = useSetup2FAMutation();
  const [verify2FASetup] = useVerify2FASetupMutation();
  const [disable2FA] = useDisable2FAMutation();
  const [regenerateBackupCodes] = useRegenerateBackupCodesMutation();

  const [qrData, setQrData] = useState<{ qrCode: string; secret: string } | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });
  const [regenCode, setRegenCode] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);

  // Change password
  const [changePassword] = useChangePasswordMutation();
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Отладочное логирование
  React.useEffect(() => {
    console.log("🔍 Профиль пользователя:", profile);
    if (profile?.id) {
      console.log("🔍 Данные тарифов:", {
        traderId: profile.id,
        tariffs,
        tariffsError,
        tariffsLoading,
      });
    } else {
      console.log("❌ ID трейдера не найден в профиле");
    }
  }, [profile, tariffs, tariffsError, tariffsLoading]);

  const [terminateSession] = useTerminateSessionMutation();
  const [logoutAllOtherSessions] = useLogoutAllOtherSessionsMutation();

  const exportData: any = null;
  const isExporting = false;

  const handleLogoutSession = async (sessionId: string) => {
    try {
      await terminateSession(sessionId);
    } catch (error) {
      console.error("Ошибка завершения сессии:", error);
    }
  };

  const handleLogoutAllSessions = async () => {
    try {
      await logoutAllOtherSessions();
    } catch (error) {
      console.error("Ошибка завершения всех сессий:", error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseUserAgent = (userAgent: string) => {
    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari")) return "Safari";
    if (userAgent.includes("Edge")) return "Edge";
    return "Неизвестный";
  };



  // Функции для экспорта
  const handleExportFormChange = (field: string, value: string) => {
    setExportForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Функция для переключения выбора типа экспорта
  const toggleExportType = (exportType: string) => {
    setSelectedExportTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(exportType)) {
        newSet.delete(exportType);
      } else {
        newSet.add(exportType);
      }
      return newSet;
    });
  };

  // Вспомогательные функции для экспорта
  const getMimeType = (format: string): string => {
    switch (format.toLowerCase()) {
      case "csv":
        return "text/csv";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      case "json":
        return "application/json";
      default:
        return "application/octet-stream";
    }
  };

  const getTypeDisplayName = (type: string): string => {
    const typeNames: { [key: string]: string } = {
      PAYIN_ORDERS: "Ордера (payin)",
      PAYOUT_ORDERS: "Ордера (payout)",
      WORKING_BALANCE_HISTORY: "Рабочий баланс",
      DEPOSITS_WITHDRAWALS_HISTORY: "Пополнения/Выводы",
      TRANSIT_BALANCE_HISTORY: "Переходной баланс",
      APPEALS: "Апелляции",
    };
    return typeNames[type] || type;
  };

  // Создание ZIP архива для нескольких файлов
  const createZipArchive = async (exportResults: any[]) => {
    console.log("📦 Создаем ZIP архив с файлами:", exportResults.length);

    const zip = new JSZip();

    exportResults.forEach((result) => {
      let fileData = result.data;
      let fileName = `${getTypeDisplayName(result.type)}.${result.format}`;

      console.log(`📄 Добавляем в архив: ${fileName}`, {
        format: result.format,
        dataType: typeof fileData,
        isArrayBuffer: fileData instanceof ArrayBuffer,
        isUint8Array: fileData instanceof Uint8Array,
        dataLength:
          typeof fileData === "string" ? fileData.length : "не строка",
      });

      // Обрабатываем разные типы данных
      if (result.format === "json" && typeof fileData !== "string") {
        // JSON данные - преобразуем в строку
        fileData = JSON.stringify(fileData, null, 2);
      } else if (result.format === "xlsx") {
        // Excel файлы могут приходить в разных форматах
        if (typeof fileData === "string") {
          // Если это base64 строка, конвертируем в Buffer
          try {
            if (fileData.startsWith("data:")) {
              const base64Data = fileData.split(",")[1];
              fileData = Uint8Array.from(atob(base64Data), (c) =>
                c.charCodeAt(0)
              );
            } else {
              fileData = Uint8Array.from(atob(fileData), (c) =>
                c.charCodeAt(0)
              );
            }
          } catch (e) {
            console.warn(
              "Не удалось декодировать Excel данные как base64, используем как есть"
            );
          }
        } else if (
          fileData &&
          typeof fileData === "object" &&
          fileData.type === "Buffer" &&
          Array.isArray(fileData.data)
        ) {
          // Если это Buffer объект от Node.js (через JSON)
          console.log("Обнаружен Buffer объект, конвертируем в Uint8Array");
          fileData = new Uint8Array(fileData.data);
        } else if (fileData instanceof ArrayBuffer) {
          // Если это ArrayBuffer
          console.log("Обнаружен ArrayBuffer, конвертируем в Uint8Array");
          fileData = new Uint8Array(fileData);
        } else if (fileData instanceof Uint8Array) {
          // Уже в правильном формате
          console.log("Данные уже в формате Uint8Array");
        } else {
          console.warn(
            "Неизвестный формат Excel данных:",
            typeof fileData,
            fileData?.constructor?.name
          );
        }
      }
      // CSV данные используем как есть (строка)

      zip.file(fileName, fileData);
    });

    console.log("🔄 Генерируем ZIP архив...");
    const zipBlob = await zip.generateAsync({ type: "blob" });

    const timestamp = new Date()
      .toISOString()
      .slice(0, 16)
      .replace(/[:-]/g, "");
    const zipFileName = `export_${timestamp}.zip`;

    console.log(`⬇️ Скачиваем ZIP архив: ${zipFileName}`);
    downloadFile(zipBlob, zipFileName, "application/zip");
  };

  // Функция для пакетного экспорта выбранных типов
  const handleBatchExport = async () => {
    if (selectedExportTypes.size === 0) {
      alert("Выберите хотя бы один тип данных для экспорта");
      return;
    }

    console.log("🚀 Начинаем пакетный экспорт:", {
      selectedTypes: Array.from(selectedExportTypes),
      format: exportForm.format,
      dateFrom: exportForm.dateFrom,
      dateTo: exportForm.dateTo,
    });

    try {
      const exportResults: Array<{
        type: string;
        data: any;
        filename: string;
        format: string;
        rawData: any;
      }> = [];

      // Экспортируем каждый выбранный тип
      for (const exportType of Array.from(selectedExportTypes)) {
        console.log(`📤 Экспортируем тип: ${exportType}`);

        // Используем оригинальный формат для каждого файла
        const requestFormat = exportForm.format;

        const exportParams = {
          exportType,
          format: requestFormat,
          ...(exportForm.dateFrom && {
            dateFrom: new Date(exportForm.dateFrom).toISOString(),
          }),
          ...(exportForm.dateTo && {
            dateTo: new Date(exportForm.dateTo).toISOString(),
          }),
        };

        console.log(`📋 Параметры экспорта для ${exportType}:`, exportParams);

        const result = await exportData(exportParams).unwrap();
        console.log(`✅ Результат экспорта для ${exportType}:`, {
          type: exportType,
          format: result.format,
          dataType: typeof result.data,
          dataConstructor: result.data?.constructor?.name,
          isArrayBuffer: result.data instanceof ArrayBuffer,
          isUint8Array: result.data instanceof Uint8Array,
          dataKeys:
            typeof result.data === "object"
              ? Object.keys(result.data || {})
              : "не объект",
          dataPreview:
            typeof result.data === "string"
              ? result.data.substring(0, 100)
              : typeof result.data === "object" && result.data
                ? JSON.stringify(result.data).substring(0, 200)
                : "не строка/объект",
          hasFilename: !!result.filename,
          filename: result.filename,
        });

        exportResults.push({
          type: exportType,
          data: result.data,
          filename: result.filename || `${exportType}_${Date.now()}`,
          format: result.format || requestFormat.toLowerCase(),
          rawData: result, // Сохраняем полный ответ от API
        });
      }

      console.log("📦 Обработка результатов экспорта:", {
        resultsCount: exportResults.length,
        format: exportForm.format,
        results: exportResults.map((r) => ({
          type: r.type,
          format: r.format,
          filename: r.filename,
        })),
      });

      // Обрабатываем результаты в зависимости от количества файлов
      if (exportResults.length === 1) {
        // Один файл - загружаем как есть
        const result = exportResults[0];
        const mimeType = getMimeType(result.format);
        console.log("⬇️ Загружаем один файл:", {
          filename: result.filename,
          mimeType,
        });
        downloadFile(result.data, result.filename, mimeType);
      } else {
        // Несколько файлов - всегда создаем ZIP архив
        console.log("📦 Создаем ZIP архив для множественных файлов");
        await createZipArchive(exportResults);
      }

      // Очищаем выбранные типы после успешного экспорта
      console.log("🧹 Очищаем выбранные типы");
      setSelectedExportTypes(new Set());
    } catch (error: any) {
      console.error("❌ Ошибка пакетного экспорта:", error);
      console.error("❌ Детали ошибки:", {
        message: error?.message || "Unknown error",
        stack: error?.stack,
        selectedTypes: Array.from(selectedExportTypes),
        exportForm,
      });
      alert(
        `Произошла ошибка при экспорте данных: ${
          error?.message || "Неизвестная ошибка"
        }`
      );
    }
  };

  // Оставляем старую функцию для совместимости (если потребуется)
  const handleExportData = async (exportType: string) => {
    try {
      const exportParams = {
        exportType,
        format: exportForm.format,
        ...(exportForm.dateFrom && {
          dateFrom: new Date(exportForm.dateFrom).toISOString(),
        }),
        ...(exportForm.dateTo && {
          dateTo: new Date(exportForm.dateTo).toISOString(),
        }),
      };

      const result = await exportData(exportParams).unwrap();

      // Обрабатываем результат в зависимости от формата
      if (result.format === "csv") {
        downloadFile(result.data, result.filename, "text/csv");
      } else if (result.format === "xlsx") {
        downloadFile(
          result.data,
          result.filename,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
      } else if (result.format === "json") {
        const jsonData = JSON.stringify(result.data, null, 2);
        downloadFile(jsonData, `export_${Date.now()}.json`, "application/json");
      }
    } catch (error) {
      console.error("Ошибка экспорта:", error);
      alert("Произошла ошибка при экспорте данных");
    }
  };

  const downloadFile = (
    data: string | Buffer | ArrayBuffer | Uint8Array | Blob,
    filename: string,
    mimeType: string
  ) => {
    console.log("⬇️ downloadFile вызван:", {
      filename,
      mimeType,
      dataType: typeof data,
      isBlob: data instanceof Blob,
    });

    try {
      let blob: Blob;

      // Если уже Blob, используем как есть
      if (data instanceof Blob) {
        blob = data;
        console.log("📦 Используем существующий Blob:", {
          size: blob.size,
          type: blob.type,
        });
      } else {
        let blobData: any = data;

        // Если это строка и это Excel файл, конвертируем в Uint8Array
        if (typeof data === "string" && mimeType.includes("spreadsheet")) {
          // Для Excel файлов, которые пришли как base64 строка
          if (data.startsWith("data:")) {
            // Убираем data:... префикс
            const base64Data = data.split(",")[1];
            blobData = Uint8Array.from(atob(base64Data), (c) =>
              c.charCodeAt(0)
            );
          } else {
            // Если это просто base64 строка
            try {
              blobData = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
            } catch (e) {
              console.warn(
                "Не удалось декодировать base64, используем как есть"
              );
              blobData = data;
            }
          }
        }

        blob = new Blob([blobData], { type: mimeType });
        console.log("📦 Создан новый blob:", {
          size: blob.size,
          type: blob.type,
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      console.log("🔗 Создана ссылка для скачивания:", {
        href: url,
        download: filename,
      });

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("✅ Файл успешно скачан:", filename);
    } catch (error: any) {
      console.error("❌ Ошибка при скачивании файла:", error);
      alert(
        `Ошибка при скачивании файла ${filename}: ${error?.message || "Неизвестная ошибка"}`
      );
    }
  };

  const renderGeneralSettings = () => (
    <SettingsGrid>
      <SettingsCard>
        <CardTitle>Настройки вебхуков</CardTitle>

        <FormGroup>
          <Label>URL для уведомлений</Label>
          <Input type="url" placeholder="https://your-domain.com/webhook" />
        </FormGroup>

        <FormGroup>
          <Label>Секретный ключ</Label>
          <Input type="password" placeholder="Введите секретный ключ" />
        </FormGroup>

        <FormGroup>
          <Label>События для отправки</Label>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              marginTop: "0.5rem",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              <input type="checkbox" defaultChecked />
              Изменение статуса ордера
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              <input type="checkbox" defaultChecked />
              Пополнение баланса
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              <input type="checkbox" />
              Создание апелляции
            </label>
          </div>
        </FormGroup>

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
          <ActionButton variant="primary">Сохранить</ActionButton>
          <ActionButton variant="secondary">Тест</ActionButton>
        </div>
      </SettingsCard>

      <SettingsCard
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <CardTitle data-testid="export-section">Экспорт данных</CardTitle>

        <FormGroup>
          <Label>Период выгрузки</Label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Input
              type="date"
              style={{ width: "auto" }}
              value={exportForm.dateFrom}
              onChange={(e) =>
                handleExportFormChange("dateFrom", e.target.value)
              }
            />
            <span style={{ color: "var(--text-secondary)" }}>—</span>
            <Input
              type="date"
              style={{ width: "auto" }}
              value={exportForm.dateTo}
              onChange={(e) => handleExportFormChange("dateTo", e.target.value)}
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Label>Формат файла</Label>
          <Select
            value={exportForm.format}
            onChange={(e) => handleExportFormChange("format", e.target.value)}
          >
            <option value="CSV">CSV</option>
            <option value="XLSX">Excel (XLSX)</option>
            <option value="JSON">JSON</option>
          </Select>
        </FormGroup>

        {/* Выбираемые кнопки экспорта */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "0.5rem",
            marginTop: "auto",
            paddingTop: "1rem",
          }}
        >
          <SelectableExportButton
            selected={selectedExportTypes.has("PAYIN_ORDERS")}
            onClick={() => toggleExportType("PAYIN_ORDERS")}
            disabled={isExporting}
          >
            Ордера (payin)
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("PAYOUT_ORDERS")}
            onClick={() => toggleExportType("PAYOUT_ORDERS")}
            disabled={isExporting}
          >
            Ордера (payout)
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("WORKING_BALANCE_HISTORY")}
            onClick={() => toggleExportType("WORKING_BALANCE_HISTORY")}
            disabled={isExporting}
          >
            Рабочий баланс
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("DEPOSITS_WITHDRAWALS_HISTORY")}
            onClick={() => toggleExportType("DEPOSITS_WITHDRAWALS_HISTORY")}
            disabled={isExporting}
          >
            Пополнения/Выводы
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("TRANSIT_BALANCE_HISTORY")}
            onClick={() => toggleExportType("TRANSIT_BALANCE_HISTORY")}
            disabled={isExporting}
          >
            Переходной баланс
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("APPEALS")}
            onClick={() => toggleExportType("APPEALS")}
            disabled={isExporting}
          >
            Апелляции
          </SelectableExportButton>
        </div>

        {/* Кнопка загрузки */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "1rem",
            paddingTop: "1rem",
            borderTop: "1px solid var(--border-color)",
          }}
        >
          <ActionButton
            variant="primary"
            onClick={handleBatchExport}
            disabled={isExporting || selectedExportTypes.size === 0}
            style={{
              fontSize: "1rem",
              padding: "0.75rem 2rem",
              minWidth: "200px",
            }}
          >
            {isExporting
              ? "Загрузка..."
              : `Загрузить${selectedExportTypes.size > 0 ? ` (${selectedExportTypes.size})` : ""}`}
          </ActionButton>
        </div>
      </SettingsCard>
    </SettingsGrid>
  );



  const renderTariffsSettings = () => (
    <div>
      <div
        style={{
          marginBottom: "1rem",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: "600",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Тарифы трейдера
        </h2>
        <p
          style={{
            fontSize: "0.875rem",
            color: "var(--text-secondary)",
            margin: "0.25rem 0 0 0",
          }}
        >
          Тарифы назначаются администратором в админ панели
        </p>
      </div>

      <SettingsCard
        style={{
          padding: 0,
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      >
        <TariffTable>
          <TariffHeader>
            <div>Название</div>
            <div>Банк</div>
            <div>Метод</div>
            <div>Комиссия</div>
            <div>Сумма</div>
            <div></div>
          </TariffHeader>

          {tariffsLoading ? (
            <EmptyState>
              <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
                ⏳
              </div>
              <div>Загрузка тарифов...</div>
            </EmptyState>
          ) : tariffsError ? (
            <EmptyState>
              <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
                ❌
              </div>
              <div>Ошибка загрузки тарифов</div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginTop: "0.5rem",
                }}
              >
                {JSON.stringify(tariffsError)}
              </div>
            </EmptyState>
          ) : !tariffs?.data || tariffs.data.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
                💰
              </div>
              <div>Тарифы не настроены</div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginTop: "0.5rem",
                }}
              >
                Обратитесь к администратору для настройки тарифов
              </div>
            </EmptyState>
          ) : (
            tariffs.data.map((tariff: any) => (
              <TariffRow key={tariff.id}>
                <div style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                  {tariff.name}
                </div>
                <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  {tariff.bank || "Любой"}
                </div>
                <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  {tariff.method || "Любой"}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--accent-neutral-hover)",
                    fontWeight: "600",
                  }}
                >
                  {tariff.feePercentage}%
                </div>
                <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  {tariff.minAmount && tariff.maxAmount
                    ? `${tariff.minAmount.toLocaleString()}-${tariff.maxAmount.toLocaleString()} ${tariff.currency}`
                    : tariff.minAmount
                      ? `от ${tariff.minAmount.toLocaleString()} ${tariff.currency}`
                      : tariff.maxAmount
                        ? `до ${tariff.maxAmount.toLocaleString()} ${tariff.currency}`
                        : "Любая"}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Badge variant={tariff.isActive ? "success" : "error"}>
                    {tariff.isActive ? "Активен" : "Неактивен"}
                  </Badge>
                </div>
              </TariffRow>
            ))
          )}
        </TariffTable>
      </SettingsCard>
    </div>
  );

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Заполните все поля");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error("Новый пароль должен содержать минимум 6 символов");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Новый пароль и подтверждение не совпадают");
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(passwordForm).unwrap();
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast.success("Пароль успешно изменён");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка при смене пароля");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const result = await setup2FA().unwrap();
      setQrData({ qrCode: result.qrCode, secret: result.secret });
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка при генерации QR-кода");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (setupCode.length < 6) { toast.error("Введите 6-значный код"); return; }
    setTwoFALoading(true);
    try {
      const result = await verify2FASetup({ code: setupCode }).unwrap();
      setBackupCodes(result.backupCodes);
      setQrData(null);
      setSetupCode("");
      refetch2FA();
      toast.success("2FA успешно активирована!");
    } catch (err: any) {
      toast.error(err?.data?.message || "Неверный код");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableForm.password || !disableForm.code) { toast.error("Заполните все поля"); return; }
    setTwoFALoading(true);
    try {
      await disable2FA(disableForm).unwrap();
      setDisableForm({ password: "", code: "" });
      refetch2FA();
      toast.success("2FA отключена");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleRegenCodes = async () => {
    if (regenCode.length < 6) { toast.error("Введите код"); return; }
    setTwoFALoading(true);
    try {
      const result = await regenerateBackupCodes({ code: regenCode }).unwrap();
      setBackupCodes(result.backupCodes);
      setRegenCode("");
      refetch2FA();
      toast.success("Резервные коды перегенерированы");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка");
    } finally {
      setTwoFALoading(false);
    }
  };

  const renderSecuritySettings = () => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "1.5rem" }}>
      <SettingsCard>
        <CardTitle>Смена пароля</CardTitle>
        <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Для смены пароля введите текущий пароль и новый пароль дважды.
        </div>
        <FormGroup>
          <Label>Текущий пароль</Label>
          <Input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))} placeholder="Введите текущий пароль" />
        </FormGroup>
        <FormGroup>
          <Label>Новый пароль</Label>
          <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))} placeholder="Минимум 6 символов" />
        </FormGroup>
        <FormGroup>
          <Label>Подтверждение нового пароля</Label>
          <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} placeholder="Повторите новый пароль" />
        </FormGroup>
        <ActionButton onClick={handleChangePassword} disabled={passwordLoading} style={{ marginTop: "0.75rem" }}>
          {passwordLoading ? "Сохранение..." : "Изменить пароль"}
        </ActionButton>
      </SettingsCard>

      <SettingsCard>
        <CardTitle>Двухфакторная аутентификация (2FA)</CardTitle>

        {twoFAStatus?.enabled ? (
          <>
            <Badge variant="success">2FA активна</Badge>
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "var(--text-secondary)" }}>
              Резервных кодов: {twoFAStatus.backupCodesRemaining}
            </div>

            <div style={{ marginTop: "1.5rem" }}>
              <CardTitle style={{ fontSize: "0.95rem" }}>Перегенерировать резервные коды</CardTitle>
              <FormGroup>
                <Label>Текущий код 2FA</Label>
                <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/[^0-9]/g, ""))} maxLength={6} placeholder="000000" />
              </FormGroup>
              <ActionButton onClick={handleRegenCodes} disabled={twoFALoading} style={{ marginTop: "0.75rem" }}>Перегенерировать</ActionButton>
            </div>

            <div style={{ marginTop: "2rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
              <CardTitle style={{ fontSize: "0.95rem", color: "var(--accent-error)" }}>Отключить 2FA</CardTitle>
              <FormGroup>
                <Label>Пароль</Label>
                <Input type="password" value={disableForm.password} onChange={(e) => setDisableForm((p) => ({ ...p, password: e.target.value }))} placeholder="Ваш пароль" />
              </FormGroup>
              <FormGroup>
                <Label>Код 2FA</Label>
                <Input value={disableForm.code} onChange={(e) => setDisableForm((p) => ({ ...p, code: e.target.value.replace(/[^0-9]/g, "") }))} maxLength={6} placeholder="000000" />
              </FormGroup>
              <ActionButton variant="danger" onClick={handleDisable2FA} disabled={twoFALoading} style={{ marginTop: "0.75rem" }}>Отключить 2FA</ActionButton>
            </div>
          </>
        ) : qrData ? (
          <>
            <div style={{ background: "var(--alert-warning-bg)", padding: "1rem", borderRadius: "0.5rem", fontSize: "0.875rem", color: "var(--alert-warning-text)", marginBottom: "1rem" }}>
              Отсканируйте QR-код в Google Authenticator, затем введите 6-значный код.
            </div>
            <div style={{ textAlign: "center", margin: "1rem 0" }}>
              <img src={qrData.qrCode} alt="QR Code" style={{ width: 200, height: 200, borderRadius: "0.5rem" }} />
            </div>
            <div style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-secondary)", wordBreak: "break-all", marginBottom: "1rem" }}>
              Или введите вручную: <strong>{qrData.secret}</strong>
            </div>
            <FormGroup>
              <Label>Код из приложения</Label>
              <Input value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/[^0-9]/g, ""))} maxLength={6} placeholder="000000" style={{ textAlign: "center", fontSize: "1.25rem", letterSpacing: "0.3rem" }} />
            </FormGroup>
            <ActionButton onClick={handleVerify2FA} disabled={twoFALoading}>Подтвердить и активировать</ActionButton>
          </>
        ) : (
          <>
            <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
              Двухфакторная аутентификация добавляет дополнительный уровень защиты. При входе помимо пароля потребуется код из Google Authenticator.
            </div>
            <ActionButton onClick={handleSetup2FA} disabled={twoFALoading}>
              {twoFALoading ? "Загрузка..." : "Настроить 2FA"}
            </ActionButton>
          </>
        )}
      </SettingsCard>

      {backupCodes && (
        <SettingsCard>
          <CardTitle>Резервные коды</CardTitle>
          <div style={{ background: "var(--alert-warning-bg)", border: "1px solid var(--accent-warning)", padding: "1rem", borderRadius: "0.5rem", fontSize: "0.875rem", color: "var(--alert-warning-text)", marginBottom: "1rem" }}>
            <strong>Важно!</strong> Сохраните эти коды в безопасном месте. Каждый код можно использовать только один раз.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", fontFamily: "monospace", fontSize: "0.95rem" }}>
            {backupCodes.map((code, i) => (
              <div key={i} style={{ padding: "0.5rem", background: "var(--bg-secondary)", borderRadius: "0.25rem", textAlign: "center" }}>{code}</div>
            ))}
          </div>
          <ActionButton variant="secondary" onClick={() => setBackupCodes(null)} style={{ marginTop: "1rem" }}>Скрыть коды</ActionButton>
        </SettingsCard>
      )}
    </div>
  );

  return (
    <SettingsContainer>
      <TabsContainer>
        <Tab
          active={activeTab === "general"}
          onClick={() => setActiveTab("general")}
        >
          Общие настройки
        </Tab>

        <Tab
          active={activeTab === "security"}
          onClick={() => setActiveTab("security")}
        >
          Безопасность
        </Tab>

        <Tab
          active={activeTab === "tariffs"}
          onClick={() => setActiveTab("tariffs")}
        >
          Тарифы
        </Tab>
      </TabsContainer>

      <TabContent>
        {activeTab === "general" && renderGeneralSettings()}
        {activeTab === "security" && renderSecuritySettings()}
        {activeTab === "tariffs" && renderTariffsSettings()}
      </TabContent>

    </SettingsContainer>
  );
}

export default Settings;
